/**
 * LegalReviewShelf — Consultation slide.
 *
 * Redesigned shelf with radio-selectable consultation matters, context textarea,
 * estimated turnaround, and trust banner.
 *
 * Per amended legal-review-access spec (2026-05-23, tasks 11.E.4 + 11.E.5):
 *   - The shelf body is UNIFORM across tiers + credit states. The previously-
 *     rendered inline credits-exhausted block is removed; both states now route
 *     through the in-shelf submit click via `tierActions.resolveConsultationAction`.
 *   - The submit button stays enabled in every state. Click behavior is
 *     governed by the matrix:
 *       - Anonymous              → signup redirect.
 *       - Free / Pro             → openWithEntry({ entry: 'consultation' })
 *       - Counsel-with-credit    → POST store-referral-lead.
 *       - Unlimited-with-credit  → POST store-referral-lead.
 *       - Counsel-exhausted      → openWithEntry({ entry: 'consultation',
 *                                  purchaseTarget: { kind: 'one_time',
 *                                  transactionType: 'legal_consultation' } }).
 *       - Unlimited-exhausted    → same one-time purchase as Counsel-exhausted
 *                                  (additional-consultation-one-time-charge).
 */
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { Zap, Star, Clock, BadgeCheck, ChevronDown, ChevronUp, ArrowDown, Info, BriefcaseBusiness } from "lucide-react";
import InhouseLoader from "src/components/InhouseLoader/InhouseLoader";
import MultiAttorneyAvatars from "src/pages/chat-page/components/MultiAttorneyAvatars";

import plus100kConsultation from "src/images/plus-100k-consultation.png";
import DrawerSkeleton from "./DrawerSkeleton";
import { trackAnalytics } from "../../helpers";
import { PLANS, PLANS_V3 } from "src/utils/Constants";
import { TIER_DESCRIPTORS } from "src/domain/plan/descriptors";
import {
  BUTTON_ROLES,
  BUTTON_TYPES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  MODAL_NAMES,
} from "src/utils/EventConstants";
import {
  getUserMetadata,
  resetStoreReferralStatus,
  setRequestLegalReviewAsideDetails,
  setShowSignupToUpgradeModal,
  storeReferral,
} from "src/store/slices/onboardingSlice";
import { resolveConsultationAction } from "src/utils/tierActions";
import { openWithEntry } from "src/store/slices/checkoutFlowSlice";
import { persistConsultationDescription } from "src/utils/checkoutPersistence";
import { CONSULTATION_NO_DETAILS_FALLBACK } from "src/utils/consultationDescription";
import { broadcastSubscriptionUpdated } from "src/utils/crossTabSync";
import { getAskThreadMessages } from "src/store/slices/messageSlice";
import { readConsultationDescription, clearConsultationDescription } from "src/utils/checkoutPersistence";






const Consultation = () => {
  const dispatch = useDispatch();
  const [additionalText, setAdditionalText] = useState("");
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const faqSectionRef = useRef(null);

  const Email = useSelector((state) => state.homeslice.email);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const threadData = useSelector((state) => state.messageSlice.threadData);
  const requestLegalReviewAsideDetails = useSelector((state) => state.onboarding.requestLegalReviewAsideDetails);
  const storeReferralStatus = useSelector((state) => state.onboarding.storeReferralStatus);
  const storeReferralErrorCode = useSelector((state) => state.onboarding.storeReferralErrorCode);

  const eligibleOffers = requestLegalReviewAsideDetails?.btn?.eligible_offers || {};

  const handoffContext = requestLegalReviewAsideDetails?.btn?.lawyer_handoff_context;



  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  // INH-5289: Restore description saved before the signup redirect so the
  // shelf shows the text the user typed before they left.
  useEffect(() => {
    const threadId = threadData?.id || requestLegalReviewAsideDetails?.threadId || null;
    const saved = readConsultationDescription(threadId);
    if (saved) {
      setAdditionalText(saved);
      clearConsultationDescription();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps








  // Reset draft text when the drawer is closed + track open/close transitions.
  const prevShelfShowRef = useRef(null);
  useEffect(() => {
    const show = requestLegalReviewAsideDetails?.show;
    if (prevShelfShowRef.current === null) {
      prevShelfShowRef.current = show;
    } else if (show && !prevShelfShowRef.current) {
      trackAnalytics(EVENT_NAMES.VIEWED_MODAL, {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CONSULTATION_SHELF,
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.THREAD_ID]: threadData?.id || requestLegalReviewAsideDetails?.threadId,
      });
    } else if (!show && prevShelfShowRef.current) {
      trackAnalytics(EVENT_NAMES.CLOSED_MODAL, {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CONSULTATION_SHELF,
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.THREAD_ID]: threadData?.id || requestLegalReviewAsideDetails?.threadId,
      });
      setAdditionalText("");
    }
    if (!show) {
      setAdditionalText("");
    }
    prevShelfShowRef.current = show;
  }, [requestLegalReviewAsideDetails?.show]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Post-referral handling ───────────────────────────────────────────────
  useEffect(() => {
    if (storeReferralStatus === "success") {
      const tid = threadData?.id || requestLegalReviewAsideDetails?.threadId;
      if (tid) {
        broadcastSubscriptionUpdated({ kind: "consultation_consumed", threadId: tid });
        dispatch(getAskThreadMessages({ threadId: tid, dispatchSource: "storeReferral-success", force: true }));
      }
      // INH-5025: confirmation toast on store-referral-lead success
      toast(
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <BriefcaseBusiness size={16} className="text-[#0A0A0A] flex-shrink-0" />
            <span className="font-sans text-sm font-semibold leading-5 text-[#0A0A0A]">
              Consultation confirmed
            </span>
          </div>
          <span className="font-sans text-sm font-normal leading-5 text-[#0A0A0A]">
            Check your email for guidance and next steps. A lawyer will reach out within 2 days.
          </span>
        </div>,
        {
          toastId: "consultation-confirmed",
          autoClose: 6000,
          icon: false,
          hideProgressBar: true,
          closeButton: true,
          style: {
            background: "#FFFFFF",
            borderRadius: "6px",
            border: "1px solid #E5E5E5",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            padding: "16px 24px 16px 16px",
            width: "388px",
          },
        },
      );

      dispatch(resetStoreReferralStatus());
      dispatch(setRequestLegalReviewAsideDetails({ show: false }));
    } else if (storeReferralStatus === "failed") {
      if (storeReferralErrorCode === "consultation_credits_exhausted") {
        const tid = threadData?.id || requestLegalReviewAsideDetails?.threadId;
        dispatch(getUserMetadata());
        if (tid) {
          broadcastSubscriptionUpdated({ kind: "consultation_consumed", threadId: tid });
        }
        dispatch(resetStoreReferralStatus());
      }
    }
  }, [storeReferralStatus]); // eslint-disable-line

  // ─── CTA: "Loop in your lawyer" ───────────────────────────────────────────
  const handleLoopInCounsel = async () => {
    const button = requestLegalReviewAsideDetails?.btn || null;
    const currentThreadId = threadData?.id || requestLegalReviewAsideDetails?.threadId;
    const resolvedDocId = button?.docId || null;
    const action = resolveConsultationAction(userMetadata, button?.eligible_offers);

    // The description is optional (INH-4988): no arm blocks on an empty
    // textarea. Submission paths that deliver notes to the BE fall back to
    // CONSULTATION_NO_DETAILS_FALLBACK so the consultation still registers.
    const descriptionProvided = Boolean(additionalText.trim());

    trackAnalytics(
      EVENT_NAMES.CLICKED_BUTTON,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
        [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
        [EVENT_PARAMETERS.BUTTON_TEXT]: "Loop in your lawyer",
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.LEGAL_REVIEW,
        [EVENT_PARAMETERS.LEGAL_REVIEW_CREDITS_REMAINING]: userMetadata?.legal_review_credits_remaining ?? 0,
        [EVENT_PARAMETERS.CONSULTATION_DESCRIPTION_PROVIDED]: descriptionProvided,
      },
      userMetadata,
    );

    switch (action.type) {
      case "signup_redirect": {
        localStorage.setItem("action_type", "consult_attorney");
        if (button) {
          try {
            localStorage.setItem("legal_review_btn", JSON.stringify(button));
          } catch (_) { /* storage full — shelf will fall back to defaults */ }
        }
        // Persist the description so it survives the signup redirect and can
        // be restored on the post-auth checkout slide (INH-5289).
        if (additionalText.trim()) {
          persistConsultationDescription({ threadId: currentThreadId || null, description: additionalText });
        }
        dispatch(setRequestLegalReviewAsideDetails({ show: false }));
        dispatch(
          setShowSignupToUpgradeModal({
            show: true,
            plans: PLANS.SUBSCRIBER_ENTERPRISE,
            slide: "signup",
          }),
        );
        return;
      }
      case "submit_referral": {
        const fullName = `${userMetadata?.first_name || ""} ${userMetadata?.last_name || ""}`.trim();
        const phoneDigits = (userMetadata?.phone_number || "").replace(/^\+1/, "");
        // Empty notes fall back so the lead still carries a description for
        // the lawyer (INH-4988 — the description is optional for the user).
        const description = descriptionProvided
          ? `What should the lawyer focus on: ${additionalText}`
          : CONSULTATION_NO_DETAILS_FALLBACK;
        const formData = {
          name: fullName,
          email: Email,
          phone: phoneDigits,
          thread_id: currentThreadId,
          description,
          state: userMetadata?.individual_info?.state_of_residence || "",
          referral_category: button?.referral_category || "business",
          lawyer_consultation_offer:
            button?.eligible_offers?.lawyer_consultation || eligibleOffers?.lawyer_consultation || "default",
          type: "consultation",
          doc_id: resolvedDocId,
        };
        dispatch(storeReferral(formData));
        return;
      }
      case "direct_checkout": {
        trackAnalytics(EVENT_NAMES.INITIATED_CHECKOUT, {
          [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
          [EVENT_PARAMETERS.CHECKOUT_FLOW]: "consultation",
          [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
          [EVENT_PARAMETERS.TARGET_PLAN_TIER]: action.tier ?? undefined,
          [EVENT_PARAMETERS.THREAD_ID]: currentThreadId || null,
          [EVENT_PARAMETERS.LEGAL_REVIEW_CREDITS_REMAINING]: userMetadata?.legal_review_credits_remaining ?? 0,
        });
        // Survive a page refresh mid-checkout: the URL restores the payment,
        // this restores the notes (consultation-contract-rev3).
        persistConsultationDescription({ threadId: currentThreadId || null, description: additionalText });
        dispatch(
          openWithEntry({
            entry: "consultation",
            threadId: currentThreadId || null,
            selectedTier: action.tier,
            initialStep: "checkout",
            // Ride the open payload — openAt resets additionalInfo, so a
            // separate setAdditionalInfo dispatched before openWithEntry is
            // wiped (consultation-contract-rev3 erratum, found live-testing).
            additionalInfo: additionalText,
            consultationDocId: resolvedDocId,
            consultationReferralCategory: button?.referral_category || "business",
            consultationSubheader: shelfTitle,
          }),
        );
        dispatch(setRequestLegalReviewAsideDetails({ show: false }));
        return;
      }
      case "purchase_additional_consultation": {
        // Both credits-exhausted tiers (Counsel and legacy Unlimited) land
        // here — a $149 one-time consultation purchase, replacing the old
        // Unlimited-upsell and the Unlimited-exhausted reset modal. See
        // openspec/changes/additional-consultation-one-time-charge/.
        trackAnalytics(EVENT_NAMES.INITIATED_CHECKOUT, {
          [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
          [EVENT_PARAMETERS.CHECKOUT_FLOW]: "consultation",
          [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
          [EVENT_PARAMETERS.TARGET_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
          [EVENT_PARAMETERS.THREAD_ID]: currentThreadId || null,
          [EVENT_PARAMETERS.LEGAL_REVIEW_CREDITS_REMAINING]: userMetadata?.legal_review_credits_remaining ?? 0,
        });
        // Survive a page refresh mid-checkout: the URL restores the payment,
        // this restores the notes (consultation-contract-rev3).
        persistConsultationDescription({ threadId: currentThreadId || null, description: additionalText });
        dispatch(
          openWithEntry({
            entry: "consultation",
            threadId: currentThreadId || null,
            purchaseTarget: { kind: "one_time", transactionType: "legal_consultation" },
            initialStep: "checkout",
            // Same as the direct_checkout arm: the description must ride the
            // open payload to survive openAt's additionalInfo reset.
            additionalInfo: additionalText,
            consultationDocId: resolvedDocId,
            consultationReferralCategory: button?.referral_category || "business",
            consultationSubheader: shelfTitle,
          }),
        );
        dispatch(setRequestLegalReviewAsideDetails({ show: false }));
        return;
      }
      case "not_available":
      default:
        return;
    }
  };

  // ─── "Scroll down for FAQs" link ──────────────────────────────────────────
  const handleScrollToFaqs = () => {
    trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
      [EVENT_PARAMETERS.BUTTON_TEXT]: "Scroll down for FAQs",
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.SCROLL_TO_FAQS,
      [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CONSULTATION_SHELF,
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
    });
    faqSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (isLoading) {
    return <DrawerSkeleton />;
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  const shelfTitle = handoffContext?.subheader || requestLegalReviewAsideDetails?.btn?.text || "Discuss your matter with a lawyer";

  const FAQ_ITEMS = [
    {
      question: "What kind of advice can your attorneys provide?",
      answer: "Inhouse attorneys are generalists. They are licensed in New York, California, and Michigan, but can advise clients in other states when the issues don't turn on local law. They learn your goals, check in, and develop strategies to achieve your objectives.",
    },
    {
      question: "What if I need help with an active dispute?",
      answer: "Inhouse attorneys won't be familiar with your local court rules, so they can't research and advise on local law. They can however advise you on strategy, negotiation, and general principles of law. If you need somebody to show up in court or negotiate on your behalf, they can find one.",
    },
    {
      question: "What if I need a specialist?",
      answer: "If our Inhouse attorneys lack the expertise, we can connect you with an attorney from our network, including all 50 states and practice areas. We negotiate a discount rate and can manage the relationship so you don't have to worry about finding the right person or paying a good price.",
    },
    {
      question: "What can your attorneys do in one hour consultation?",
      answer: "Your lawyer will review the chat and take note of the documents you uploaded or generated. They have only 30 minutes to prepare for the call so they'll get through what they can if its long. On the call, they give advice for you to utilize to finish the work yourself.",
    },
    {
      question: "What are your Member Rates",
      answer: "If you need more than a consultation, your lawyer can offer a flat rate to do the work. For example, they can often send a letter from their letterhead and negotiate a settlement, draft a customer agreement from scratch, or finalize and format a financing document.",
    },
  ];

  return (
    <div className="w-full flex flex-col h-full relative overflow-hidden">
      {/* ─── Header (fixed) ─── */}
      <div className="flex-shrink-0 flex flex-col md:!flex-row md:!items-center items-start gap-2 md:!gap-[16px] px-[16px] py-[14px] md:!px-[24px] bg-[#F4F3EF]">
        <MultiAttorneyAvatars size="lg" className="!mr-0 !h-[32px] !w-[72px] md:!h-[36px] md:!w-[82px]" />
        <h2
          data-testid="consultation-title"
          className="font-['Zodiak'] font-[540] text-[20px] md:!text-[24px] leading-[130%] text-[#0F0F0F] m-0"
        >
          {shelfTitle}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain">
        {/* ─── Above-the-fold: fills visible height ─── */}
        <div className="min-h-full flex flex-col justify-between">
          <div>
            {/* ─── Benefits ─── */}
            <div className="flex flex-col gap-[14px] md:!gap-[28px] px-[16px] md:!px-[24px] pt-2 md:!pt-4 pb-4">
              <div className="flex items-start gap-2">
                <div className="shrink-0 flex items-center justify-center w-[28px] h-[28px] md:!w-[32px] md:!h-[32px] rounded-[8px] bg-[#F4F3EF] mt-0.5">
                  <Zap size={18} className="text-[#111111]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
                    <span className="md:hidden">Fast, affordable feedback</span>
                    <span className="hidden md:inline">Fast feedback and clear next steps</span>
                  </p>
                  <p className="font-['DM_Sans'] font-normal text-[14px] text-[#7A7A7A] m-0 mt-1 leading-[140%]">
                    <span className="md:hidden">Get 30 minutes of review and 30 minutes of live feedback on your issue.</span>
                    <span className="hidden md:inline">Get 1 hour of attorney time on your issue. They'll spend up to 30 minutes on preparation, then walk you through their feedback on a 30 minute call.</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="shrink-0 flex items-center justify-center w-[28px] h-[28px] md:!w-[32px] md:!h-[32px] rounded-[8px] bg-[#F4F3EF] mt-0.5">
                  <Star size={18} className="text-[#111111]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
                    <span className="md:hidden">Top-tier expertise</span>
                    <span className="hidden md:inline">Work with an elite attorney</span>
                  </p>
                  <p className="font-['DM_Sans'] font-normal text-[14px] text-[#7A7A7A] m-0 mt-1 leading-[140%]">
                    <span className="md:hidden">Elite lawyers from top law firms. Experienced with multi-state matters. <Info size={14} className="text-[#737373] inline-block align-middle" /></span>
                    <span className="hidden md:inline">We'll assign you a top lawyer from our law firm, Inhouse Counsel PC. They'll serve as your family or business' general counsel.</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="shrink-0 flex items-center justify-center w-[28px] h-[28px] md:!w-[32px] md:!h-[32px] rounded-[8px] bg-[#F4F3EF] mt-0.5">
                  <BadgeCheck size={18} className="text-[#111111]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
                    <span className="md:hidden">A real firm behind you</span>
                    <span className="hidden md:inline">Legal work you can actually afford</span>
                  </p>
                  <p className="font-['DM_Sans'] font-normal text-[14px] text-[#7A7A7A] m-0 mt-1 leading-[140%]">
                    <span className="md:hidden">Inhouse Counsel, PC stands behind all advice, guaranteeing its professional quality.</span>
                    <span className="hidden md:inline">If you need more than a consult, your lawyer can offer flat fee discounted legal work or refer to our vetted network for a specialist.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* ─── Focus textarea ─── */}
            <div className="flex flex-col gap-[8px] px-[16px] md:!px-[24px] pb-6">
              <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
                What should the lawyer focus on?{" "}
                <span className="font-normal text-[#737373]">(optional)</span>
              </p>
              <textarea
                data-testid="consultation-additional-details"
                className="border px-3 py-3 h-[100px] md:!h-[120px] w-full rounded-[8px] bg-white text-[13px] md:!text-[14px] font-['DM_Sans'] placeholder:text-[#9B9B9B] focus:outline-none resize-none border-[#E4E4E4] focus:border-[#A3A3A3] focus:shadow-[0_0_0_3px_rgba(0,0,0,0.06)]"
                value={additionalText}
                disabled={storeReferralStatus === "loading"}
                placeholder="What parts of this chat and the documents do you want the lawyer to focus on during their 30-minute prep for the call?"
                onChange={(e) => setAdditionalText(e.target.value)}
              />
              <div className="flex items-center gap-[6px]">
                <Clock size={14} className="text-[#7A7A7A] flex-shrink-0" />
                <p className="font-['DM_Sans'] font-normal text-[14px] leading-[18px] text-[#7A7A7A] m-0">
                  Lawyer will respond within <span className="font-normal text-[#197635]">2 business days</span>
                </p>
              </div>
            </div>
          </div>

          {/* ─── CTA section (desktop only — mobile is sticky below) ─── */}
          <div className="hidden md:!flex flex-col items-center gap-[12px] px-[24px] pb-6">
            <button
              data-testid="consultation-cta-button-desktop"
              className="flex justify-center items-center w-full max-w-[543px] h-[46px] px-[16px] rounded-[8px] bg-[#141414] text-white font-['DM_Sans'] font-medium text-[15px] hover:bg-[#000] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={storeReferralStatus === "loading"}
              onClick={handleLoopInCounsel}
            >
              {storeReferralStatus === "loading" ? (
                <div className="flex items-center justify-center gap-4">
                  <div className="w-4 h-4 border-2 border-t-0 border-white rounded-full animate-spin" />
                </div>
              ) : (
                "Request consultation"
              )}
            </button>

            {(userMetadata?.subscription_type === PLANS_V3.INHOUSE_COUNSEL || userMetadata?.subscription_type === PLANS_V3.INHOUSE_COUNSEL_PLUS) ? (
              <div data-testid="consultation-credits-remaining-desktop" className="flex flex-col items-center gap-[4px]">
                <p className="font-['DM_Sans'] font-semibold text-[12px] leading-[100%] text-[#242424] m-0 text-center">
                  {userMetadata?.legal_review_credits_remaining ?? 0}/{TIER_DESCRIPTORS[userMetadata?.subscription_type]?.monthlyTasks ?? 1} Monthly Consultations Available
                </p>
                {userMetadata?.subscription_renewal_date && (
                  <p className="font-['DM_Sans'] font-normal text-[12px] leading-[100%] text-[#737373] m-0 text-center">
                    Next Free Consultation: {new Date(userMetadata.subscription_renewal_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-[6px]">
                <img src={plus100kConsultation} alt="Attorney avatars" className="h-[24px] w-[54px]" />
                <p className="font-['DM_Sans'] font-medium text-[14px] text-[#0F0F0F] m-0 text-center leading-[150%]">100K+ Consultations delivered</p>
              </div>
            )}

            <div className="w-full border-t border-[#E5E5E5]" />
            <button
              type="button"
              data-testid="consultation-scroll-to-faqs"
              className="flex items-center gap-[6px] cursor-pointer bg-transparent border-0 p-0"
              onClick={handleScrollToFaqs}
            >
              <p className="font-['DM_Sans'] font-normal text-[14px] text-[#6B7280] m-0">Frequently asked questions</p>
              <ArrowDown size={14} className="text-[#6B7280]" />
            </button>
          </div>
        </div>

        {/* ─── Sticky mobile CTA — sticks at bottom, scrolls away to reveal FAQs ─── */}
        <div className="md:!hidden sticky bottom-0 z-10 flex flex-col items-center gap-[10px] px-[16px] py-[12px] border-t border-[#E5E5E5] bg-white">
          <button
            data-testid="consultation-cta-button"
            className="flex justify-center items-center w-full h-[40px] md:!h-[46px] px-[16px] rounded-[8px] bg-[#141414] text-white font-['DM_Sans'] font-medium text-[14px] hover:bg-[#000] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={storeReferralStatus === "loading"}
            onClick={handleLoopInCounsel}
          >
            {storeReferralStatus === "loading" ? (
              <div className="flex items-center justify-center gap-4">
                <div className="w-4 h-4 border-2 border-t-0 border-white rounded-full animate-spin" />
              </div>
            ) : (
              "Request consultation"
            )}
          </button>

          {(userMetadata?.subscription_type === PLANS_V3.INHOUSE_COUNSEL || userMetadata?.subscription_type === PLANS_V3.INHOUSE_COUNSEL_PLUS) ? (
            <div data-testid="consultation-credits-remaining" className="flex flex-col items-center gap-[4px]">
              <p className="font-['DM_Sans'] font-semibold text-[12px] leading-[100%] text-[#242424] m-0 text-center">
                {userMetadata?.legal_review_credits_remaining ?? 0}/{TIER_DESCRIPTORS[userMetadata?.subscription_type]?.monthlyTasks ?? 1} Monthly Consultations Available
              </p>
              {userMetadata?.subscription_renewal_date && (
                <p className="font-['DM_Sans'] font-normal text-[12px] leading-[100%] text-[#737373] m-0 text-center">
                  Next Free Consultation: {new Date(userMetadata.subscription_renewal_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-[6px]">
              <img src={plus100kConsultation} alt="Attorney avatars" className="h-[24px] w-[54px]" />
              <p className="font-['DM_Sans'] font-medium text-[14px] text-[#0F0F0F] m-0 text-center leading-[150%]">100K+ Consultations delivered</p>
            </div>
          )}

          <div className="w-full border-t border-[#E5E5E5]" />
          <button
            type="button"
            className="flex items-center gap-[6px] cursor-pointer bg-transparent border-0 p-0"
            onClick={handleScrollToFaqs}
          >
            <p className="font-['DM_Sans'] font-normal text-[14px] text-[#6B7280] m-0">Frequently asked questions</p>
            <ArrowDown size={14} className="text-[#6B7280]" />
          </button>
        </div>

        {/* ─── FAQ Accordion (below the fold) ─── */}
        <div ref={faqSectionRef} className="px-[16px] md:!px-[24px] pb-6" data-testid="consultation-faq-section">
          <div className="border-t border-[#E4E4E4]">
            {FAQ_ITEMS.map((item, idx) => (
              <div key={idx} className="border-b border-[#E4E4E4]">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-[16px] cursor-pointer bg-transparent border-0 text-left"
                  onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                >
                  <p className="font-['DM_Sans'] font-medium text-[14px] text-[#0A0A0A] m-0 pr-4">{item.question}</p>
                  {openFaqIndex === idx ? (
                    <ChevronUp size={18} className="text-[#141414] shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-[#141414] shrink-0" />
                  )}
                </button>
                {openFaqIndex === idx && (
                  <p className="font-['DM_Sans'] font-normal text-[14px] text-[#737373] m-0 pb-[16px] leading-[160%]">
                    {item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* ─── Still have questions ─── */}
          <div className="flex items-center gap-[6px] mt-[16px]">
            <Info size={14} className="text-[#6B7280] shrink-0" />
            <p className="font-['DM_Sans'] font-normal text-[13px] text-[#6B7280] m-0">
              Still have questions?{" "}
              <a href="https://www.inhouse.ai/faqs" target="_blank" rel="noopener" className="underline text-[#6B7280] hover:text-[#141414]">
                Learn more about how we can help.
              </a>
            </p>
          </div>
        </div>
      </div>

      {storeReferralStatus === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
          <InhouseLoader size={60} />
          <p className="font-sans text-[14px] text-[#686868] mt-2">Sending your request...</p>
        </div>
      )}
    </div>
  );
};

export default Consultation;
